import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/format'
import { SearchIcon, XIcon } from './icons'

export interface SearchInputProps {
  /** The committed (debounced) value, e.g. from the URL. */
  value: string
  /** Called `delay` ms after typing stops, and at once on clear/Enter. */
  onChange: (value: string) => void
  /** Accessible name. */
  label: string
  placeholder?: string
  delay?: number
  className?: string
  /** For a visible <label htmlFor> (FormField) above the box. */
  id?: string
}

/**
 * Search box with debounce: one request after typing stops, not one per
 * keystroke. Clear (×, or Escape) and Enter apply immediately. Follows
 * `value` when it changes from outside (e.g. a Reset button or Back).
 */
export function SearchInput({ value, onChange, label, placeholder, delay = 300, className, id }: SearchInputProps) {
  const [text, setText] = useState(value)
  const lastEmitted = useRef(value)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // External change (Reset, Back): adopt it.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value
      setText(value)
    }
  }, [value])

  // Debounced emit.
  useEffect(() => {
    if (text === lastEmitted.current) return
    const t = setTimeout(() => {
      if (text === lastEmitted.current) return // already applied by Enter/Clear
      lastEmitted.current = text
      onChangeRef.current(text)
    }, delay)
    return () => clearTimeout(t)
  }, [text, delay])

  const emitNow = (next: string) => {
    setText(next)
    lastEmitted.current = next
    onChangeRef.current(next)
  }

  return (
    <div className={cn('relative', className)}>
      <SearchIcon size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle" />
      <input
        id={id}
        type="search"
        role="searchbox"
        aria-label={label}
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && text) {
            e.preventDefault()
            emitNow('')
          }
          if (e.key === 'Enter') {
            e.preventDefault()
            emitNow(text)
          }
        }}
        className={cn(
          'h-9 w-full rounded-md border border-line-strong bg-raised pl-9 pr-9 text-[13.5px] text-ink placeholder:text-subtle max-md:text-base',
          'hover:border-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25 focus-visible:outline-none',
          '[&::-webkit-search-cancel-button]:hidden',
        )}
      />
      {text ? (
        <button
          type="button"
          onClick={() => emitNow('')}
          aria-label="Clear search"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1.5 text-muted hover:bg-hover hover:text-ink"
        >
          <XIcon size={14} />
        </button>
      ) : null}
    </div>
  )
}
